import type { IsoDate } from '@contracts';

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatBannerDate(date: IsoDate): string {
  const [y, m, d] = date.split('-').map(Number);
  const jsDate = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  return `${WEEKDAY[jsDate.getUTCDay()]}, ${MONTH[jsDate.getUTCMonth()]} ${jsDate.getUTCDate()}`;
}

/**
 * The persistent banner (today-view.md §1.7) that replaces the header's
 * kicker row entirely whenever the rendered date is not today -- the one
 * thing that must never be ambiguous about which day is being viewed.
 * "Catching up on," never "You forgot"/"Missing"/"Incomplete" -- the single
 * most load-bearing microcopy decision in the backfill design.
 *
 * No padding of its own -- rendered inside TodayHeader's already-padded
 * <header>, occupying exactly the kicker row's slot (the two are never both
 * visible, so there is no double-padding to reconcile).
 */
export function BackfillBanner({
  date,
  onBackToToday,
}: {
  readonly date: IsoDate;
  readonly onBackToToday: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
      }}
    >
      <span
        className="num"
        aria-live="polite"
        style={{
          fontSize: 'var(--type-label-size)',
          lineHeight: 'var(--type-label-line)',
          fontWeight: 600,
          color: 'var(--hue-primary)',
        }}
      >
        Catching up on {formatBannerDate(date)}
      </span>
      <button
        type="button"
        onClick={onBackToToday}
        style={{
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-2)',
          fontSize: 'var(--type-label-size)',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          flex: 'none',
        }}
      >
        Back to today
      </button>
    </div>
  );
}
